import type { CurrencyCode } from '@klear/constants/currency'
import { KlearDate } from '@klear/date'
import { Timestamp } from '@klear/time'
import { Money } from '@klear/money'
import type { PaymentAccountType, PaymentNetwork, PaymentStatus, PaymentType } from '@klear/payment/constants'
import { placeholderAccountSides } from '@klear/payable/placeholder-account'
import type { PaymentItem, PaymentItemInput } from '@klear/payment/item'
import type { PaymentLineItem } from '@klear/payment/line-item'
import type { PayablePublishAction } from '@klear/payable/constants'
import { payableLineItemsFrom, type PayableEventAllocation } from '@klear/payable/line-items'
import { payableIdDisplay, type PayableListBucket } from '@klear/payable/list-display'
import type { OperationalRead } from '@klear/operational/operational-read'
import {
  paymentCanAmend,
  paymentIsDraft,
  paymentIsEditable,
  paymentIsProcessing,
  paymentIsScheduled
} from '@klear/payment/status'

export type PayableOrderFields = {
  orderId?: number
  orderIdDisplay?: string
  customerName?: string
}

export type PaymentFields = {
  paymentId?: number
  idDisplay?: string
  status: PaymentStatus
  number?: string
  amount: Money
  effectiveDate: KlearDate
  notes?: string
  internalNotes?: string
  statementDescriptor?: string
  notifySupplier?: boolean
  type: PaymentType
  network: PaymentNetwork
  sourceAccountType: PaymentAccountType
  sourceAccountId: number
  sourceAccountOrganizationId: number
  sourceAccountOrganizationName: string
  destinationAccountType: PaymentAccountType
  destinationAccountId: number
  destinationAccountOrganizationId: number
  destinationAccountOrganizationName: string
}

export type PayableRelationFields = {
  operationals: OperationalRead[]
  items?: PaymentItemInput[]
  eventAllocations?: PayableEventAllocation[]
  supplierName?: string
  resolvedSupplierId?: number
}

export type PayableCommitFields = {
  publishAction?: PayablePublishAction
}

export type PayableExecution = {
  status?: string
  stripePaymentId?: string
  stripePaymentErrorMessage?: string
  receiptUrl?: string
  postedAt?: Timestamp
}

export type PayableActor = {
  firstName?: string
  lastName?: string
}

export type PayableReadFields = {
  execution?: PayableExecution
  createdAt?: Timestamp
  updatedAt?: Timestamp
  createdBy?: PayableActor
  traderId?: number
  traderOrganizationId?: number
  last4?: string
}

export type PayableJson = PayableOrderFields &
  PaymentFields &
  PayableRelationFields &
  PayableCommitFields &
  PayableReadFields & {
    id?: number
    currency?: CurrencyCode
  }

export class Payable {
  static readonly __klearClass = 'Payable'

  readonly orderId?: number
  readonly orderIdDisplay?: string
  readonly customerName?: string

  id?: number
  paymentId?: number
  readonly idDisplay: string
  status: PaymentStatus
  number?: string
  amount: Money
  effectiveDate: KlearDate
  notes?: string
  internalNotes?: string
  statementDescriptor?: string
  notifySupplier: boolean
  type: PaymentType
  network: PaymentNetwork
  sourceAccountType: PaymentAccountType
  sourceAccountId: number
  sourceAccountOrganizationId: number
  sourceAccountOrganizationName: string
  destinationAccountType: PaymentAccountType
  destinationAccountId: number
  destinationAccountOrganizationId: number
  destinationAccountOrganizationName: string

  operationals: OperationalRead[]
  items: PaymentItem[]
  eventAllocations: PayableEventAllocation[]
  lineItems: PaymentLineItem[]
  supplierName?: string
  resolvedSupplierId?: number

  publishAction?: PayablePublishAction

  execution?: PayableExecution
  createdAt?: Timestamp
  updatedAt?: Timestamp
  createdBy?: PayableActor
  traderId?: number
  traderOrganizationId?: number
  last4?: string

  constructor(props: PayableJson) {
    this.orderId = props.orderId || undefined
    this.orderIdDisplay = props.orderIdDisplay
    this.customerName = props.customerName

    this.id = props.id
    this.paymentId = props.paymentId
    this.idDisplay = payableIdDisplay(props.idDisplay)
    this.status = props.status
    this.number = props.number
    this.amount = props.amount
    this.effectiveDate = props.effectiveDate
    this.notes = props.notes
    this.internalNotes = props.internalNotes
    this.statementDescriptor = props.statementDescriptor
    this.notifySupplier = props.notifySupplier ?? false
    this.type = props.type
    this.network = props.network
    this.sourceAccountType = props.sourceAccountType
    this.sourceAccountId = props.sourceAccountId
    this.sourceAccountOrganizationId = props.sourceAccountOrganizationId
    this.sourceAccountOrganizationName = props.sourceAccountOrganizationName
    this.destinationAccountType = props.destinationAccountType
    this.destinationAccountId = props.destinationAccountId
    this.destinationAccountOrganizationId = props.destinationAccountOrganizationId
    this.destinationAccountOrganizationName = props.destinationAccountOrganizationName

    this.operationals = props.operationals
    this.eventAllocations = props.eventAllocations ?? []
    this.items = (props.items ?? []).map((item) => ({
      ...item,
      amount: new Money(item.amount)
    }))
    this.lineItems = payableLineItemsFrom(
      this.operationals,
      this.items,
      props.eventAllocations ?? [],
      this.amount.currency
    )
    this.supplierName = props.supplierName
    this.resolvedSupplierId = props.resolvedSupplierId

    this.publishAction = props.publishAction

    this.execution = props.execution
    this.createdAt = props.createdAt
    this.updatedAt = props.updatedAt
    this.createdBy = props.createdBy
    this.traderId = props.traderId
    this.traderOrganizationId = props.traderOrganizationId
    this.last4 = props.last4
  }

  get currency(): CurrencyCode {
    return this.amount.currency
  }

  get isPersistedDraft(): boolean {
    return this.paymentId != null && this.paymentId > 0 && paymentIsDraft(this.status)
  }

  get isScheduled(): boolean {
    return this.paymentId != null && this.paymentId > 0 && paymentIsScheduled(this.status)
  }

  get canSave(): boolean {
    return paymentIsDraft(this.status) || this.isScheduled
  }

  get hasPlaceholderAccount(): boolean {
    return placeholderAccountSides(this).length > 0
  }

  get canPublish(): boolean {
    return this.isPersistedDraft && !this.hasPlaceholderAccount
  }

  get effectiveStatus(): string {
    return this.paymentStatus ?? this.status
  }

  get listBucket(): PayableListBucket {
    if (this.isSent) return 'sent'
    if (paymentIsDraft(this.status)) return 'draft'
    return 'scheduled'
  }

  get canComplete(): boolean {
    return !this.isSent && !this.hasPlaceholderAccount && (this.canPayNow || paymentIsEditable(this.effectiveStatus))
  }

  get canMarkPaid(): boolean {
    return !this.isSent && paymentIsEditable(this.effectiveStatus)
  }

  get canEdit(): boolean {
    return !this.isSent && paymentIsEditable(this.effectiveStatus)
  }

  get canDelete(): boolean {
    return paymentCanAmend({ status: this.effectiveStatus, stripePaymentId: this.stripePaymentId })
  }

  get canPayNow(): boolean {
    return this.isPersistedDraft && !this.hasPlaceholderAccount
  }

  get primaryOperational(): OperationalRead | undefined {
    return this.operationals[0]
  }

  get operationalId(): number {
    const id = this.primaryOperational?.id
    return id != null && id > 0 ? id : 0
  }

  get purchaseOrderId(): number | undefined {
    const id = this.primaryOperational?.parentEventId
    return id != null && id > 0 ? id : undefined
  }

  get supplierId(): number | undefined {
    return this.resolvedSupplierId
  }

  get operationalTitle(): string | undefined {
    const title = this.primaryOperational?.title
    return title != null && title.length > 0 ? title : undefined
  }

  get paymentStatus(): string | undefined {
    return this.execution?.status
  }

  get paymentType(): PaymentType {
    return this.type
  }

  get paymentNetwork(): PaymentNetwork {
    return this.network
  }

  get stripePaymentId(): string | undefined {
    return this.execution?.stripePaymentId
  }

  get stripePaymentErrorMessage(): string | undefined {
    return this.execution?.stripePaymentErrorMessage
  }

  get receiptUrl(): string | undefined {
    return this.execution?.receiptUrl
  }

  get postedAt(): Timestamp | undefined {
    return this.execution?.postedAt
  }

  get isSent(): boolean {
    return this.execution?.stripePaymentId != null && this.execution.stripePaymentId.length > 0
  }

  get isSending(): boolean {
    if (this.isSent) return false
    const status = this.effectiveStatus
    if (paymentIsProcessing(status)) return true
    return paymentIsScheduled(status) && this.effectiveDate.isSameOrBefore(KlearDate.today())
  }

  withProps(patch: Partial<PayableJson>): Payable {
    return new Payable({ ...payableJson(this), ...patch })
  }

  toJSON(): PayableJson {
    return payableJson(this)
  }
}

function payableJson(payable: Payable): PayableJson {
  return {
    orderId: payable.orderId,
    orderIdDisplay: payable.orderIdDisplay,
    customerName: payable.customerName,
    id: payable.id,
    paymentId: payable.paymentId,
    idDisplay: payable.idDisplay,
    status: payable.status,
    number: payable.number,
    amount: payable.amount,
    effectiveDate: payable.effectiveDate,
    notes: payable.notes,
    internalNotes: payable.internalNotes,
    statementDescriptor: payable.statementDescriptor,
    notifySupplier: payable.notifySupplier,
    type: payable.type,
    network: payable.network,
    sourceAccountType: payable.sourceAccountType,
    sourceAccountId: payable.sourceAccountId,
    sourceAccountOrganizationId: payable.sourceAccountOrganizationId,
    sourceAccountOrganizationName: payable.sourceAccountOrganizationName,
    destinationAccountType: payable.destinationAccountType,
    destinationAccountId: payable.destinationAccountId,
    destinationAccountOrganizationId: payable.destinationAccountOrganizationId,
    destinationAccountOrganizationName: payable.destinationAccountOrganizationName,
    operationals: payable.operationals,
    items: payable.items,
    eventAllocations: payable.eventAllocations,
    supplierName: payable.supplierName,
    resolvedSupplierId: payable.resolvedSupplierId,
    publishAction: payable.publishAction,
    execution: payable.execution,
    createdAt: payable.createdAt,
    updatedAt: payable.updatedAt,
    createdBy: payable.createdBy,
    traderId: payable.traderId,
    traderOrganizationId: payable.traderOrganizationId,
    last4: payable.last4
  }
}
